import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListApprovalProcessesResult,
  type ListValidationRulesResult,
  type OpenApprovalInboxResult,
} from '@whinerp/client';
import type { RuleNode } from '@whinerp/contracts/rules';
import { Button, EmptyState, ErrorState, PageHeading, Panel, RuleBuilder, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { ruleBuilderLabels } from '../catalog/rule-labels.js';
import { defineScreen } from '../types.js';
import { ApprovalChainView } from './approval-chain.js';

/**
 * SC-68 Approvals (F-090).
 *
 * The approver's inbox - each request with its whole chain, approve or reject
 * with a comment, or hand it on - and the approver's own "while I am away".
 * Administrators define the processes below it.
 */

type Chain = OpenApprovalInboxResult['requests'][number];
type Process = ListApprovalProcessesResult['processes'][number];
type Step = Process['steps'][number];
type ObjectKey = Process['objectKey'];
const OBJECTS: ObjectKey[] = ['deal', 'quote', 'account', 'contact', 'lead'];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';
const recordHref = (chain: Chain): string =>
  ({ deal: `/deals/${chain.recordId}`, account: `/companies/${chain.recordId}`, contact: `/contacts/${chain.recordId}`, lead: `/leads/${chain.recordId}`, quote: `/quotes/${chain.recordId}` })[
    chain.objectKey
  ] ?? '/';

function ApprovalsScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const { can } = useSession();
  const inbox = useQuery<OpenApprovalInboxResult, ApiError>({
    queryKey: ['approval-inbox'],
    queryFn: () => api.openApprovalInbox({ body: {} }),
  });
  const directory = useQuery<ListApprovalProcessesResult, ApiError>({
    queryKey: ['approval-processes'],
    queryFn: () => api.listApprovalProcesses({}),
  });

  if (inbox.isPending) return <Skeleton className="h-plot w-full" />;
  if (inbox.isError) return <ErrorState title={t('state.error')} body={errorText(inbox.error)} onRetry={() => void inbox.refetch()} />;

  return (
    <div data-component="Approvals" className="flex w-full flex-col gap-4">
      <PageHeading>{t('approvals.title')}</PageHeading>
      {inbox.data.requests.length === 0 ? (
        <EmptyState reason="all-clear" title={t('approvals.inboxEmpty')} body={t('approvals.inboxEmptyBody')} />
      ) : (
        <ul data-approval-inbox className="flex flex-col gap-3">
          {inbox.data.requests.map((chain) => (
            <li key={chain.requestId}>
              <InboxItem chain={chain} people={directory.data?.people ?? []} />
            </li>
          ))}
        </ul>
      )}
      <DelegationPanel current={inbox.data.delegation} people={directory.data?.people ?? []} />
      {can('setup.roles.manage') && directory.data ? <ProcessesPanel directory={directory.data} /> : null}
    </div>
  );
}

function InboxItem({ chain, people }: { chain: Chain; people: ListApprovalProcessesResult['people'] }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [handTo, setHandTo] = useState('');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['approval-inbox'] });
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api.decideApprovalStep({ path: { requestId: chain.requestId }, body: { decision, comment: comment.trim() || null } }),
    onSuccess: refresh,
  });
  const delegate = useMutation({
    mutationFn: () => api.delegateApprovalStep({ path: { requestId: chain.requestId }, body: { toUserId: handTo, comment: comment.trim() || null } }),
    onSuccess: refresh,
  });
  const refusal = decide.error ?? delegate.error;
  return (
    <Panel data-inbox-request={chain.recordName}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={recordHref(chain)} className="text-body text-ink hover:text-bronze-text">{chain.recordName}</Link>
          <span className="text-meta text-ink-meta">
            {chain.processName} · {t('approvals.askedBy', { values: { name: chain.requestedByName ?? '' } })}
          </span>
        </div>
        <ApprovalChainView chain={chain} />
        {refusal ? <p data-approval-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
        <div className="flex flex-wrap items-center gap-2">
          <input data-inbox-comment aria-label={t('approvals.comment')} placeholder={t('approvals.comment')} className={`${INPUT} min-w-0 flex-1`} value={comment} onChange={(e) => setComment(e.target.value)} />
          <Button intent="primary" size="small" data-inbox-approve disabled={decide.isPending} onClick={() => decide.mutate('approved')}>
            {t('approvals.approve')}
          </Button>
          <Button intent="secondary" size="small" data-inbox-reject disabled={decide.isPending} onClick={() => decide.mutate('rejected')}>
            {t('approvals.reject')}
          </Button>
          {people.length > 1 ? (
            <>
              <select data-inbox-hand-to aria-label={t('approvals.handTo')} className={INPUT} value={handTo} onChange={(e) => setHandTo(e.target.value)}>
                <option value="">{t('approvals.handTo')}</option>
                {people.map((person) => (
                  <option key={person.userId} value={person.userId}>{person.name}</option>
                ))}
              </select>
              <Button intent="ghost" size="small" data-inbox-delegate disabled={!handTo || delegate.isPending} onClick={() => delegate.mutate()}>
                {t('approvals.handOn')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}

function DelegationPanel({ current, people }: { current: OpenApprovalInboxResult['delegation']; people: ListApprovalProcessesResult['people'] }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [delegateId, setDelegateId] = useState(current?.delegateId ?? '');
  const [startsOn, setStartsOn] = useState(current?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(current?.endsOn ?? '');
  const save = useMutation({
    mutationFn: (clear: boolean) =>
      api.setApprovalDelegation({ body: { delegation: clear ? null : { delegateId, startsOn, endsOn } } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval-inbox'] }),
  });
  if (people.length < 2) return null;
  return (
    <Panel data-region="approval-delegation">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('approvals.away')}</SectionLabel>
        <p className="text-meta text-ink-meta">{current ? t('approvals.awaySet', { values: { from: current.startsOn, to: current.endsOn } }) : t('approvals.awayHelp')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('approvals.delegate')}
            <select data-delegation-to className={INPUT} value={delegateId} onChange={(e) => setDelegateId(e.target.value)}>
              <option value="">{t('approvals.chooseDelegate')}</option>
              {people.map((person) => (
                <option key={person.userId} value={person.userId}>{person.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('approvals.from')}
            <input type="date" data-delegation-from className={INPUT} value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('approvals.until')}
            <input type="date" data-delegation-until className={INPUT} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          </label>
          <Button intent="secondary" size="small" data-delegation-save disabled={!delegateId || !startsOn || !endsOn || save.isPending} onClick={() => save.mutate(false)}>
            {t('approvals.saveAway')}
          </Button>
          {current ? (
            <Button intent="ghost" size="small" data-delegation-clear disabled={save.isPending} onClick={() => save.mutate(true)}>
              {t('approvals.clearAway')}
            </Button>
          ) : null}
        </div>
        {save.error ? <p className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
      </div>
    </Panel>
  );
}

const blankStep = (): Step => ({ name: '', approverKind: 'owner-manager', approverUserIds: [], approverRoleId: null, mode: 'any', timeoutHours: null, escalateTo: null });

function ProcessesPanel({ directory }: { directory: ListApprovalProcessesResult }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [objectKey, setObjectKey] = useState<ObjectKey>('deal');
  const [conditions, setConditions] = useState<RuleNode>({ all: [] } as RuleNode);
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<Step[]>([blankStep()]);
  const schema = useQuery<ListValidationRulesResult, ApiError>({
    queryKey: ['validation-rules', objectKey],
    queryFn: () => api.listValidationRules({ path: { objectKey } }),
    enabled: editing !== null,
  });
  const save = useMutation({
    mutationFn: () =>
      api.saveApprovalProcess({
        body: { processId: editing === 'new' ? null : editing, name, objectKey, entryConditions: conditions as never, active, steps },
      }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['approval-processes'] });
    },
  });
  const open = (process: Process | null) => {
    setEditing(process?.processId ?? 'new');
    setName(process?.name ?? '');
    setObjectKey(process?.objectKey ?? 'deal');
    setConditions((process?.entryConditions as RuleNode | undefined) ?? ({ all: [] } as RuleNode));
    setActive(process?.active ?? true);
    setSteps(process?.steps.length ? process.steps : [blankStep()]);
  };
  const patch = (index: number, change: Partial<Step>) => setSteps((all) => all.map((step, i) => (i === index ? { ...step, ...change } : step)));

  return (
    <Panel data-region="approval-processes">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('approvals.processes')}</SectionLabel>
          {editing === null ? (
            <Button intent="secondary" size="small" data-process-new className="ms-auto" onClick={() => open(null)}>
              {t('approvals.newProcess')}
            </Button>
          ) : null}
        </div>
        {editing === null ? (
          directory.processes.length === 0 ? (
            <p className="text-meta text-ink-meta">{t('approvals.noProcesses')}</p>
          ) : (
            <ul className="flex flex-col">
              {directory.processes.map((process) => (
                <li key={process.processId} data-process={process.name} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                  <span className="text-dense text-ink">{process.name}</span>
                  <span className="text-meta text-ink-meta">
                    {t(`approvals.object.${process.objectKey}`)} · {t('approvals.stepCount', { values: { count: String(process.steps.length) } })}
                    {process.active ? '' : ` · ${t('approvals.inactive')}`}
                  </span>
                  <Button intent="ghost" size="small" className="ms-auto" onClick={() => open(process)}>
                    {t('approvals.edit')}
                  </Button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div data-process-editor className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('approvals.processName')}
                <input data-process-name className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('approvals.appliesTo')}
                <select data-process-object className={INPUT} value={objectKey} onChange={(e) => { setObjectKey(e.target.value as ObjectKey); setConditions({ all: [] } as RuleNode); }}>
                  {OBJECTS.map((key) => (
                    <option key={key} value={key}>{t(`approvals.object.${key}`)}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 self-end text-dense text-ink">
                <input type="checkbox" data-process-active checked={active} onChange={(e) => setActive(e.target.checked)} />
                {t('approvals.active')}
              </label>
            </div>
            <div className="flex flex-col gap-1">
              <SectionLabel>{t('approvals.criteria')}</SectionLabel>
              <p className="text-meta text-ink-meta">{t('approvals.criteriaHelp')}</p>
              {schema.data ? (
                <RuleBuilder data-process-criteria schema={schema.data.schema as never} value={conditions} onChange={setConditions} labels={ruleBuilderLabels(t)} />
              ) : (
                <Skeleton className="h-block-sm w-full" />
              )}
            </div>
            <ol className="flex flex-col gap-2">
              {steps.map((step, index) => (
                <li key={index} data-process-step={index + 1} className="flex flex-col gap-2 rounded-card border border-rule p-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <label className="flex flex-col gap-1 text-dense text-ink-600">
                      {t('approvals.stepName', { values: { number: String(index + 1) } })}
                      <input data-step-name className={INPUT} value={step.name} onChange={(e) => patch(index, { name: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-dense text-ink-600">
                      {t('approvals.approvers')}
                      <select data-step-kind className={INPUT} value={step.approverKind} onChange={(e) => patch(index, { approverKind: e.target.value as Step['approverKind'] })}>
                        <option value="owner-manager">{t('approvals.kind.owner-manager')}</option>
                        <option value="users">{t('approvals.kind.users')}</option>
                        <option value="role">{t('approvals.kind.role')}</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-dense text-ink-600">
                      {t('approvals.modeLabel')}
                      <select data-step-mode className={INPUT} value={step.mode} onChange={(e) => patch(index, { mode: e.target.value as Step['mode'] })}>
                        <option value="any">{t('approvals.mode.any')}</option>
                        <option value="all">{t('approvals.mode.all')}</option>
                      </select>
                    </label>
                  </div>
                  {step.approverKind === 'users' ? (
                    <div className="flex flex-wrap gap-2" role="group" aria-label={t('approvals.approvers')}>
                      {directory.people.map((person) => (
                        <label key={person.userId} className="flex items-center gap-1 text-dense text-ink">
                          <input
                            type="checkbox"
                            data-step-user={person.name}
                            checked={step.approverUserIds.includes(person.userId)}
                            onChange={(e) =>
                              patch(index, {
                                approverUserIds: e.target.checked
                                  ? [...step.approverUserIds, person.userId]
                                  : step.approverUserIds.filter((id) => id !== person.userId),
                              })
                            }
                          />
                          {person.name}
                        </label>
                      ))}
                    </div>
                  ) : null}
                  {step.approverKind === 'role' && directory.roles.length > 0 ? (
                    <select data-step-role aria-label={t('approvals.kind.role')} className={INPUT} value={step.approverRoleId ?? ''} onChange={(e) => patch(index, { approverRoleId: e.target.value || null })}>
                      <option value="">{t('approvals.chooseRole')}</option>
                      {directory.roles.map((role) => (
                        <option key={role.roleId} value={role.roleId}>{role.name}</option>
                      ))}
                    </select>
                  ) : null}
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="flex flex-col gap-1 text-dense text-ink-600">
                      {t('approvals.timeout')}
                      <input
                        data-step-timeout
                        inputMode="numeric"
                        className={INPUT}
                        value={step.timeoutHours === null ? '' : String(step.timeoutHours)}
                        onChange={(e) => patch(index, { timeoutHours: e.target.value.trim() === '' ? null : Number.parseInt(e.target.value, 10) || null })}
                      />
                    </label>
                    {step.timeoutHours !== null ? (
                      <label className="flex flex-col gap-1 text-dense text-ink-600">
                        {t('approvals.escalateTo')}
                        <select data-step-escalate className={INPUT} value={step.escalateTo ?? ''} onChange={(e) => patch(index, { escalateTo: e.target.value || null })}>
                          <option value="">{t('approvals.chooseDelegate')}</option>
                          {directory.people.map((person) => (
                            <option key={person.userId} value={person.userId}>{person.name}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {steps.length > 1 ? (
                      <Button intent="ghost" size="small" onClick={() => setSteps((all) => all.filter((_, i) => i !== index))}>
                        {t('approvals.removeStep')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
            {save.error ? <p data-process-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
            <div className="flex flex-wrap gap-2">
              <Button intent="ghost" size="small" data-process-add-step onClick={() => setSteps((all) => [...all, blankStep()])}>
                {t('approvals.addStep')}
              </Button>
              <Button intent="primary" size="small" data-process-save disabled={save.isPending} onClick={() => save.mutate()}>
                {t('approvals.saveProcess')}
              </Button>
              <Button intent="ghost" size="small" onClick={() => setEditing(null)}>
                {t('approvals.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function ApprovalsLoading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function ApprovalsError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('approvals.inboxEmpty')} />;
}
function ApprovalsEmpty(): JSX.Element {
  const t = useT();
  return <EmptyState reason="all-clear" title={t('approvals.inboxEmpty')} body={t('approvals.inboxEmptyBody')} />;
}

export const screen = defineScreen({
  id: 'SC-68',
  route: '/approvals',
  surface: 'page',
  title: 'Approvals',
  features: ['F-090'],
  entitlement: 'F-090',
  reachedBy: [{ kind: 'nav', item: 'approvals' }],
  components: [],
  elements: ['Approvals'],
  states: { empty: ApprovalsEmpty, loading: ApprovalsLoading, error: ApprovalsError },
  component: ApprovalsScreen,
});

export default screen;
